import ControlViewModel from '@/ViewModels/ControlViewModel';
import ComponentManager from '@/Management/ComponentManager';
import GlobalConfiguration from '@/Management/GlobalConfiguration';
import LabelViewModel from '@/ViewModels/Controls/LabelViewModel';
import ResourceManager from '@/Management/ResourceManager';
import Utilities from '@/Utility/Utilities';

import {
    GridColumn,
    GridColumnDefinition,
    GridColumnFactory,
    IGridViewModelParameters, 
    IVerticalResizeOptions,
} from "#/DefaultViewExtensionInterfaces";
import { ISortByCol, ITooltipOptions } from 'rad-coreframe-grid';
import RADApplication from '@/Management/RADApplication';
import * as NumericTypeHelper from '@/Utility/NumericTypeHelper';

export default class GridViewModel extends ControlViewModel {

    /**
    * Die Spalten
    */
    public columnDefinition: KnockoutObservableArray<GridColumnDefinition>;

    /**
     * Das Feld mit der eindeutigen Identität des Datensatzes
     */
    public identity: string;

    /**
     * Gibt an, ob das Control verzögert erzeugt werden soll
     */
    public isLazy: KnockoutObservable<boolean>;

    /**
    * Definiert die initiale Breite der Spalten
    */
    public columnPreset: KnockoutObservable<object>;

    /**
    * Sobald die Spalten verändert werden, werden die konvertierten Spalten neu berechnet
    */
    public columns: KnockoutObservable<Array<GridColumn>> | KnockoutObservableArray<GridColumn>;

    /**
     * Definiert die Id eines Row-Templates
     */
    public rowTemplate: any;

    /**
     * Definiert die Id eines RowAlt-Templates
     */
    public altRowTemplate: any;

    /**
     * Definiert die Id eines Detail-Templates
     */
    public detailTemplate;

    /**
    * Enthält die angezeigten Datensätze
    */
    public data: KnockoutObservableArray<any>;

    /**
     * Gibt an, ob das Grid scrollbar ist
     */
    public isScrollable: KnockoutObservable<boolean>;

    /**
     * Gibt an, ob das Grid editierbar ist
     */
    public isEditable: KnockoutObservable<boolean>;

    /**
     * Gibt an, ob das Grid sortierbar ist
     */
    public isSortable: KnockoutObservable<boolean>;

    /**
     * Die möglichen Seitengrößen
     */
    public pageSizes: KnockoutObservableArray<number>;

    /**
     * Die aktuelle Seitengröße
     */
    public pageSize: KnockoutObservable<number>;

    /**
     * Die aktuelle Anzahl aller Datenzeilen
     */
    public totalRows: KnockoutObservable<number>;

    /**
     * Die tatsächliche Anzahl aller Datenzeilen
     */
    public realTotalRows: KnockoutObservable<number>;

    /**
     * Gibt an, ob das Grid limitiert ist
     */
    public isLimited: KnockoutObservable<boolean>;

    /**
     * Gibt an, ob das Grid einen Pager hat
     */
    public hasPager: KnockoutObservable<boolean>;

    /**
     * Gibt an, ob das Grid eine SummaryRowhat
     */
    public hasSummaryRow: KnockoutObservable<boolean>;

    /**
     * Gibt an, ob das Grid eine TotalRow hat
     */
    public hasTotalRow: KnockoutObservable<boolean>;

    /**
     * Gibt an, ob die Tabelle selektierbar ist
     */
    public isSelectable: KnockoutObservable<boolean>;

    /**
     * Gibt an, ob die Tabelle multiselect unterstützt
     */
    public isMultiColumnSelect: KnockoutObservable<boolean>;

    /**
     * Gibt an, ob die Tabelle nach mehreren Spalten sortiert werden kann
     */
    public isMultiColumnSort: KnockoutObservable<boolean>;

    /**
     * Gibt an, ob die Sortierung aufgehoben werden kann
     */
    public allowUnsort: KnockoutObservable<boolean>;

    /**
     * Die aktuelle Seite
     */
    public page: KnockoutObservable<number>;

    /**
     * Die aktuell ausgewählten Elemente (IDs)
     */
    public selected: KnockoutObservableArray<any>;

    /**
     * Die aktuell ausgewählten Elemente (gesamte Objekte)
     */
    public selectedObjects: KnockoutObservableArray<any>;

    /**
    * Bestimmt, ob die Gesamtanzahl der Elemente angezeigt werden soll.
    */
    public extentVisible: boolean;

    /**
     * Die aktuell deaktivierten Elemente
     */
    public disabled: KnockoutObservableArray<any>;

    /**
     * Ein Overlay 
     */
    public overlay: any;

    /**
     * Das ViewModel für das "NoRightsLabel"
     */
    public noRightsLabel: LabelViewModel;

    /**
     * Das Objekt welches gehovert wird
     */
    public hovered: KnockoutObservable<any>;

    public height: KnockoutObservable<string>;
    public defaultHeight: KnockoutObservable<string>;

    /**
     * Ein Handler, der aufgerufen wird, wenn eine Zeile "gedoppelklickt" wird
     * @param {string} - Die ID der Zeile
     */
    public onDoubleClickHandler: (id: string, rowData?: any, openInNewWindow?: boolean) => any;

    /**
     * Ein Handler, der aufgerufen wird, wenn auf einer Zeile eine Taste gedrückt wird
     */
    public onKeyDownHandler: (id: any, rowData?: any) => any;

    public onSortChangedHandler: Function;

    public onSelectionChangedHandler: Function;

    /**
    * Bestimmt, ob Tabellenkopf angezeigt werden soll
    */
    public showHeader: boolean;

    protected preventRefresh: boolean;

    /**
     * Beinhaltet die Sortierung
     */
    public orderBy: KnockoutObservable<ISortByCol[]>;

    public summaryRowPreText: LabelViewModel;

    public summaryRowPostText: LabelViewModel;

    /**
    * Gibt an, ob die Daten für das Grid geladen werden können
    */
    public shouldLoadData: KnockoutObservable<boolean>;

    /**
     * Gibt an, ob das Grid asynchron oder synchron gerendert wird
     */ 
    public renderGridAsync: boolean;

    private _refreshTotalRow: KnockoutObservable<boolean>;

    public tooltipOptions: ITooltipOptions;
    
    public verticalResize: IVerticalResizeOptions;

    public showNoEntriesLabel: boolean;

    public additionalData: Record<string, any>;

    /*
    * Das ViewModel für das Grid
    * @param {object} params - Ein Objekt mit Optionen für das Control
    * @constructor
    */
    public constructor(protected params: IGridViewModelParameters) {
        super(params);

        this.preventRefresh = false;
        this.data = this.observableOrDefault(params.data, []) as KnockoutObservableArray<any>;
        this.columnDefinition = this.createColumnDefinition();
        this.identity = this.paramOrDefault(params.identity, null);
        this.isLazy = this.observableOrDefault(params.isLazy, false);
        this.columnPreset = this.observableOrDefault(params.columnPreset, {});
        this.columns = this.createColumns(params.columns);
        this.rowTemplate = this.paramOrDefault(params.rowTemplate, null);
        this.altRowTemplate = this.paramOrDefault(params.altRowTemplate, null);
        this.detailTemplate = this.paramOrDefault(params.detailTemplate, null);
        this.isScrollable = this.observableOrDefault(params.isScrollable, true);
        this.isEditable = this.observableOrDefault(params.isEditable, false);
        this.isSortable = this.observableOrDefault(params.isSortable, true);
        this.pageSizes = this.observableOrDefault(params.pageSizes, []) as KnockoutObservableArray<number>;
        this.pageSize = this.observableOrDefault(params.pageSize, 10);
        this.hasPager = this.observableOrDefault(params.hasPager, false);
        this.hasSummaryRow = this.observableOrDefault(params.hasSummaryRow, false);
        this.hasTotalRow = this.observableOrDefault(params.hasTotalRow, false);
        this.isSelectable = this.observableOrDefault(params.isSelectable, true);
        this.isMultiColumnSelect = this.observableOrDefault(params.isMultiColumnSelect, true);
        this.isMultiColumnSort = this.observableOrDefault(params.isMultiColumnSort, false);
        this.allowUnsort = this.observableOrDefault(params.allowUnsort, false);
        // Durch Umstellung auf TypeScript müssen hier 2 Parameter herangezogen werden, um kompatibel zu bleiben
        this.onDoubleClickHandler = this.handlerOrDefault(params.onDoubleClickHandler || params.onDoubleClick) as ((id: string, rowData?: any, openInNewWindow?: boolean) => any);
        this.onKeyDownHandler = this.handlerOrDefault(params.onKeyDownHandler) as ((id: any, rowData?: any) => any);
        this.page = this.observableOrDefault(params.page, 1);
        this.selected = this.observableOrDefault(params.selected, []) as KnockoutObservableArray<any>;
        this.selectedObjects = this.observableOrDefault(params.selectedObjects, []) as KnockoutObservableArray<any>;
        this.extentVisible = this.paramOrDefault(params.extentVisible, GlobalConfiguration.dataGrid.ShowCountInFooter);
        this.disabled = this.observableOrDefault(params.disabled, []) as KnockoutObservableArray<any>;
        this.overlay = this.paramOrDefault(params.overlay, null);
        this.hovered = this.observableOrDefault(params.hovered, null);
        this.height = this.observableOrDefault(params.height, null);
        this.defaultHeight = this.observableOrDefault(params.defaultHeight, '');
        // Durch Umstellung auf TypeScript müssen hier 2 Parameter herangezogen werden, um kompatibel zu bleiben
        this.onSelectionChangedHandler = this.handlerOrDefault(params.onSelectionChangedHandler || params.onChange);
        this.onSortChangedHandler = this.handlerOrDefault(params.onSortChangedHandler);
        this.totalRows = this.observableOrDefault(0);
        this.realTotalRows = this.observableOrDefault(0);
        this.isLimited = this.defineComputed(() => {
            return this.totalRows() !== this.realTotalRows();
        });
        this.showHeader = this.paramOrDefault(params.showHeader, true);
        this.orderBy = this.observableOrDefault(params.orderBy, null);
        this.shouldLoadData = this.observableOrDefault(params.shouldLoadData, true);
        this.renderGridAsync = this.paramOrDefault(params.renderGridAsync, false);

        this.noRightsLabel = this.paramOrDefault(new LabelViewModel({
            ergName: 'CURRENTLY_NO_LEGAL_ERROR',
            isInfoLabel: true,
            isVisible: this.defineComputed(() => {
                let columns = this.columns();
                let visibleColumns = _.filter(columns,
                    (column: any) => {
                        return column.hidden === false;
                    });

                let hasColumns = visibleColumns.length > 0;

                return !hasColumns;
            })
        }));

        this.tooltipOptions = $.extend({
            show: true,
            openingDelayMs: RADApplication.getInstance().getSystemParam('Gui.TooltipDelay', parseInt),
            closingDelayMs: RADApplication.getInstance().getSystemParam('Gui.TooltipClosingDelay', parseInt),
        }, params.tooltipOptions ?? {});
        
        this.summaryRowPreText = new LabelViewModel({
            ergName: 'DATAGRID_COUNT'
        });

        this.summaryRowPostText = new LabelViewModel({
            caption: this.defineComputed(() => {
                let limit = ResourceManager.getInstance().get('DATAGRID_LIMIT');
                return limit.replace('$$limit_rows$$', `${NumericTypeHelper.toLocaleString(this.totalRows(), 0)}`);
            })
        });
        this._refreshTotalRow = this.observableOrDefault(false);

        this.verticalResize = this.paramOrDefault(params.verticalResize, null);
        this.showNoEntriesLabel = this.paramOrDefault(params.showNoEntriesLabel, false);
        this.additionalData = this.paramOrDefault(params.additionalData, {});
    }
    
    protected createColumnDefinition(): KnockoutObservableArray<GridColumnDefinition> {
        return this.observableOrDefault(this.params.columnDefinition, []) as KnockoutObservableArray<GridColumnDefinition>;
    }

    protected createColumns(columns: ObservableOrDefault<GridColumn[]> | KnockoutObservableArray<GridColumn>): KnockoutObservableArray<GridColumn> | KnockoutObservable<Array<GridColumn>> {
        if (!Utilities.isNullOrUndefined(columns)) {
            return this.observableOrDefault(columns) as KnockoutObservableArray<GridColumn>;
        }
        let preset = this.columnPreset();
        return this.defineComputed(() => {
            let columns = this.columnDefinition();
            let language = (ResourceManager.getInstance().language as any).value();
            let result: Array<GridColumn> = [];

            for (const column of columns) {
                let unit = column.unitNames ? column.unitNames[language] : undefined;
                let title = this.getTitle(column, unit, language);
                let name = column.name;
                let field = column.field || (name ? name.replace(/\./g, '_').replace(/ListViewValue/, 'Value') : '');
                let hidden = !column.isVisible;
                let sortable = column.sortable || column.isSortable;
                let sortValue = column.sortValue;
                let editor = column.editor;
                let filterable = column.isFilterable;
                let isResizable = column.isResizable;
                let factoryParams = column.factoryParams;
                let uiGenerator = column.uiGenerator;
                let listViewPDOID = column.listViewPDOID;
                let width = column.width;
                let dataClass = column.dataClass;
                let value = column.value;
                let encoded = true;
                let factory = this.getFactory(column).uiGenerator;
                let onAfterCellRender = column.onAfterCellRender;
                let total = column.total;

                // Spaltenbreiten im Storage gesetzt
                if (preset[column.name] != null) {
                    width = parseInt(preset[column.name]) + '';
                }

                result.push({
                    name: name,
                    field: field,
                    title: title,
                    isSortable: sortable,
                    sortValue: sortValue,
                    editor: editor,
                    filterable: filterable,
                    isResizable: isResizable,
                    hidden: hidden,
                    factory: factory,
                    factoryParams: factoryParams,
                    dataClass: dataClass,
                    encoded: encoded,
                    uiGenerator: uiGenerator,
                    listViewPDOID: listViewPDOID,
                    width: width,
                    value: value,
                    onAfterCellRender: onAfterCellRender,
                    total: total,
                });
            }

            return result;
        });
    }

    protected getTitle(column: GridColumnDefinition, unit: string, language: string): string {
        let title = '';
        if (!Utilities.isNullOrUndefined(column.mlsErgName) && _.has(column.mlsErgName, language))
            title = column.mlsErgName[language];
        else
            title = ResourceManager.getInstance().get(column.ergName);

        if (Utilities.isNullOrUndefined(title))
            title = '';

        if (!Utilities.isNullOrUndefinedOrBlank(unit))
            title += ` [${unit}]`;

        return title;
    }

    protected getFactory(column: GridColumnDefinition): GridColumnFactory {
        let uiGenerator = column.listViewColumnFactory;
        if (column.dotNetTypeName != null && column.dotNetTypeName.indexOf('DateTime') > -1) {
            uiGenerator = 'DateTimeColumn';
        } else if (column.dotNetTypeName != null && column.dotNetTypeName.indexOf('Boolean') > -1) {
            uiGenerator = 'CheckBoxColumn';
        } else {
            uiGenerator = column.listViewColumnFactory;
        }

        return {
            uiGenerator: uiGenerator,
            parameters: null
        }
    }

    public onSortChanged(sortExpression: ISortByCol[]) {
        if (this.isDisposed)
            return;
        if (this.onSortChangedHandler)
            this.onSortChangedHandler(sortExpression);
    }

    public onDoubleClick(id: any, rowData: any, openInNewWindow: boolean) {
        if (this.isDisposed)
            return;
        if (this.onDoubleClickHandler) {
            this.onDoubleClickHandler(id, rowData, openInNewWindow);
        }
    }

    public onMouseOver(id: string, rowData: any) {
        if (this.isDisposed)
            return;

        this.hovered(rowData);
    }

    public onMouseOut() {

    }

    public onKeyDown(id: string, rowData: any) {
        if (this.isDisposed)
            return;

        if (this.onKeyDownHandler)
            this.onKeyDownHandler(id, rowData);
    }

    public onSelectionChanged(rows: Array<any>) {
        if (this.isDisposed)
            return;
        const identities = [];
        rows.forEach((row) => {
            identities.push(row[this.identity]);
        });
        this.selected(identities);
        this.selectedObjects(rows);
        if (Utilities.isFunction(this.selected.valueHasMutated))
            this.selected.valueHasMutated();
        if (this.onSelectionChangedHandler)
            this.onSelectionChangedHandler(rows);
    }

    public onPageSizeChanged(newPageSize: number): void {
        if (this.isDisposed)
            return;
        this.pageSize(newPageSize);
    }

    public onPageChanged(newPage: number): void {
        if (this.isDisposed)
            return;
        this.page(newPage);
    }

    public onColumnResized(columnName: string, newWidth: number): void {
        if (this.isDisposed)
            return;
        let preset = ko.toJS(this.columnPreset);
        preset[columnName] = newWidth + 'px';
        this.columnPreset(preset);
    }

    public static createColumnDefinition(visible: boolean, ergName: string, field: string, uiGenerator?: (raw, row) => any, dataClass?: string): GridColumnDefinition {
        return {
            name: field,
            isVisible: visible,
            ergName: ergName,
            field: field,
            isResizable: true,
            uiGenerator: uiGenerator,
            dataClass: dataClass
        };
    }

    public forceUpdateData(newData: any): void {
        const oldComparer = this.data.equalityComparer;
        this.data.equalityComparer = () => false;
        this.data(newData);
        this.data.equalityComparer = oldComparer;
    }

    public refreshTotalRow(): void {
        this._refreshTotalRow.notifySubscribers(null);
    }
}

ComponentManager.setViewModel('GridViewModel', GridViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');